import { FieldError } from '@eigtech/ui/components/form'
import { ReactNode, useCallback } from 'react'
import { FieldArrayPath, FieldValues, get, useFormState } from 'react-hook-form'
import { BaseProps, FieldControl } from '../FieldControl'
import { ArrayFieldContainer } from './ArrayFieldContainer'
import { ArrayFieldProviderProps } from './ArrayFieldContext'
import { ArrayFieldRows, ArrayFieldRowsProps } from './ArrayFieldRows'

export type ArrayFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = Pick<
  Partial<ArrayFieldRowsProps<TFieldValues, TFieldArrayName>>,
  'renderRow' | 'RowContainer' | 'RowsContainer' | 'emptyMessage'
> &
  Pick<ArrayFieldProviderProps<TFieldValues>, 'defaultValue' | 'max' | 'min'> &
  BaseProps<TFieldValues> & {
    children?: ReactNode
    name: TFieldArrayName
  }

export function ArrayField<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>({
  children,
  control,
  defaultValue,
  emptyMessage,
  max,
  min,
  renderRow,
  RowContainer,
  RowsContainer,
  ...props
}: ArrayFieldProps<TFieldValues, TFieldArrayName>) {
  const Fallback = useCallback(
    function Fallback() {
      if (renderRow) {
        return (
          <ArrayFieldRows
            RowContainer={RowContainer}
            RowsContainer={RowsContainer}
            emptyMessage={emptyMessage}
            renderRow={renderRow}
          />
        )
      }

      return <></>
    },
    [RowContainer, RowsContainer, emptyMessage, renderRow]
  )

  const { errors, isSubmitting } = useFormState({ control, name: props.name })
  const rootError = get(errors, `${props.name}.root`)
  // const isTouched = get(touchedFields, props.name)
  // const hasError = isTouched && Boolean(rootError?.message)

  return (
    <FieldControl disabled={isSubmitting} invalid={!!rootError} {...props}>
      <ArrayFieldContainer
        control={control}
        defaultValue={defaultValue}
        max={max}
        min={min}
        name={props.name}
      >
        <div className="flex flex-col gap-2">{children || <Fallback />}</div>
      </ArrayFieldContainer>

      {!!rootError?.message && <FieldError match>{rootError?.message}</FieldError>}
    </FieldControl>
  )
}

export type ArrayFieldComponent<
  TFieldValues extends FieldValues = FieldValues,
  TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> = typeof ArrayField<TFieldValues, TFieldArrayName>
